import { useState, useRef } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { UploadCloud, FileSpreadsheet, Loader2, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

export const MuqeemImportDialog = ({ open, onOpenChange, onImported }) => {
  const { dir } = useLang();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  const reset = () => { setFile(null); setResult(null); };

  const handleImport = async () => {
    if (!file) { toast.error("اختر ملف مقيم أولًا"); return; }
    setBusy(true);
    try {
      const res = await api.importMuqeem(file);
      setResult(res);
      toast.success(`تم: ${res.created} جديد · ${res.updated} محدّث`);
      onImported && onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الاستيراد");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-md font-body" dir={dir}>
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" /> استيراد من مقيم
          </DialogTitle>
          <DialogDescription className="text-start">
            ارفع ملف "المقيمين النشطين" المُصدَّر من منصة مقيم (Excel). سيتم مطابقة الموظفين برقم الإقامة وتعبئة الاسم والجنسية والمهنة ورقم الجواز وتواريخ انتهاء الإقامة والجواز وتاريخ الميلاد تلقائيًا، وإضافة غير الموجودين.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <label data-testid="muqeem-dropzone"
            className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-emerald-300 p-6 cursor-pointer hover:bg-emerald-50/50 transition">
            <UploadCloud className="h-8 w-8 text-emerald-400" />
            {file ? (
              <span className="flex items-center gap-2 text-sm text-slate-700"><FileSpreadsheet className="h-4 w-4" /> {file.name}</span>
            ) : (
              <span className="text-sm text-slate-500">اختر ملف مقيم (.xlsx)</span>
            )}
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
              data-testid="muqeem-file-input"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} />
          </label>

          {result && (
            <div data-testid="muqeem-result" className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 space-y-1">
              <p>✅ موظفون جدد: <b>{result.created}</b></p>
              <p>🔄 تم تحديثهم: <b>{result.updated}</b></p>
              <p className="text-slate-500">تم تجاهل (بدون تغيير): {result.skipped}</p>
              {result.errors?.length > 0 && (
                <p className="text-amber-700">أخطاء في {result.errors.length} صف</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="confirm-muqeem-import" disabled={busy || !file}
            className="bg-emerald-600 hover:bg-emerald-700 active:scale-95 transition gap-2" onClick={handleImport}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            {busy ? "جارٍ الاستيراد…" : "استيراد وتعبئة"}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>إغلاق</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
